/**
 * MetricTile — single-metric tile used by the page + site detail
 * views. Matches the dashboard's existing card conventions
 * (Geist Sans, rounded-xl, `--color-line` border, `text-2xl
 * font-semibold` for the hero value).
 *
 * Stack order:
 *
 *   ┌─────────────────────────────────────┐
 *   │ LCP                         ↓ 8%    │   label + optional delta
 *   │ 1.8s                                │   hero value (sans-serif)
 *   │ ▁▂▁▂▃▁▂▁▂▁▁▂▃▁▁▂▁▂▂▁                │   bars, always visible
 *   └─────────────────────────────────────┘
 *
 * The bars are stacked under the value rather than absolute-
 * positioned as a hover watermark — visible at a glance, no click
 * required. Tiles are optionally clickable (the whole card becomes
 * the hit target via the wrapping `<button>`); `selected` adds a
 * subtle ring to mark the active spotlight tile.
 */
import { CondensedBars, type CondensedBarsDatum } from '@/components/primitives/CondensedBars';
import { PlaceholderBars } from '@/components/primitives/PlaceholderBars';
import { Sparkline } from '@/components/primitives/Sparkline';
import { type MetricBand, bandChartFill, bandFill, bandText } from '@/lib/format';
import { cn } from '@/lib/utils';
import { ArrowDown, ArrowUp, Minus } from '@phosphor-icons/react';
import type { ReactElement } from 'react';

export interface MetricTileProps<TMeta = unknown> {
  label: string;
  /** Hero text — pre-formatted (`"1.8s"`, `"92"`). */
  value: string;
  /** Band drives the value tone + the bar fill colour. */
  band: MetricBand;
  /** Per-audit values used to draw the histogram. */
  bars: CondensedBarsDatum<TMeta>[];
  /**
   * Optional delta vs a comparison value. Raw percentage (positive
   * = current is higher than baseline). The tile decides direction-
   * good vs direction-bad from `lowerIsBetter`.
   */
  deltaPct?: number | null;
  /** True for LCP/CLS/INP/TTFB; false for the perf score. */
  lowerIsBetter?: boolean;
  /**
   * Tile becomes a button when set. Don't combine with `onBarClick`:
   * nested buttons aren't valid. If both are provided, only the bar
   * handler fires (tile click is suppressed).
   */
  onClick?: () => void;
  /**
   * Accessible name for the `onClick` button ("Explore LCP in Real
   * users"). Without it the button's name is the whole tile read out
   * — label, band chip and value — which says what the tile shows but
   * not what clicking does. Ignored when the tile isn't clickable.
   */
  actionLabel?: string;
  /**
   * Per-bar click handler. Each bar gains a slot-sized hit area so
   * the 2 px visual bar stays tappable. Used by the page-overview
   * tiles to navigate into the clicked audit.
   */
  onBarClick?: (datum: CondensedBarsDatum<TMeta>, index: number) => void;
  /** Per-bar tooltip text. */
  barTitle?: (datum: CondensedBarsDatum<TMeta>, index: number) => string;
  /**
   * Threshold banding for individual bars — when set, each bar takes
   * its own band colour (the CrUX "Value" treatment: a bar crossing a
   * threshold flips green → yellow → red) instead of the tile-wide
   * neutral/band fill.
   */
  barBand?: (y: number) => MetricBand;
  /** Spotlight-selected state — subtle accent ring. */
  selected?: boolean;
  className?: string;
  /**
   * Guest-site comparison footer (site-compare mode). Renders a
   * divider + one row inside the tile: a series swatch (the same hue
   * as the guest's line on the trend charts), the guest's name, and
   * its value for this metric. The relative verdict lives in the
   * hero row's `deltaPct` pill — computed against this value — so the
   * delta reads unambiguously as "this site vs the baseline below".
   */
  compare?: {
    /** Swatch colour tying the row to the guest series on charts. */
    swatchColor: string;
    /** Guest site display name. */
    label: string;
    /** Pre-formatted guest value (`'—'` when the guest has no data). */
    value: string;
  };
  /**
   * Tile colour variant — controls value tone, bar fill, and whether
   * a band status chip is shown.
   *
   *   - `'band'` (default) — legacy look. Hero value text + spark bars
   *     both take the band tint (green / amber / red). Use when a tile
   *     is shown on its own and the colour is the headline signal —
   *     e.g. the page-detail audit tiles where a single number per
   *     tile carries the verdict.
   *   - `'chip'` — used when many tiles sit side by side (e.g. the
   *     Performance overview's CWV strip). Hero value is neutral
   *     white, bars take the neutral `--color-bar` tone at low
   *     opacity (matching the Traffic page's `KpiSparkBars`), and a
   *     small status chip
   *     ("● Good" / "● Needs work" / "● Poor") in the top-right
   *     telegraphs the band. The chromatic load drops from "row of
   *     coloured numbers" to "row of small coloured chips", which the
   *     eye reads as a status strip rather than an alarm wall.
   *     Suppressed on `band === 'unknown'` (no samples → no chip).
   */
  variant?: 'band' | 'chip';
  /**
   * How the history slot draws.
   *
   *   - `'bars'` (default) — one bar per datum. Reads well for tens of
   *     points (a month of audits); at 180 daily points each bar is
   *     sub-pixel and the row degenerates into a texture.
   *   - `'line'` — a `Sparkline` path + area fill. Independent of point
   *     count, so it stays legible across a 12-month window.
   *
   * Only `'bars'` supports `onBarClick` / `barTitle` / `barBand`; a
   * line has no per-point hit areas, so those are ignored in line mode.
   */
  chart?: 'bars' | 'line';
}

/** Visible label for a CWV band, used in the chip + aria text. */
const BAND_LABEL: Record<MetricBand, string> = {
  good: 'Good',
  'needs-improvement': 'Needs work',
  poor: 'Poor',
  unknown: '',
};

export function MetricTile<TMeta = unknown>({
  label,
  value,
  band,
  bars,
  deltaPct,
  lowerIsBetter = true,
  onClick,
  actionLabel,
  onBarClick,
  barTitle,
  barBand,
  selected,
  className,
  compare,
  variant = 'band',
  chart = 'bars',
}: MetricTileProps<TMeta>): ReactElement {
  // Visual mode forks here. Chip mode neutralises the value + bars
  // and surfaces the band via a small status chip; band mode keeps
  // the legacy colour-everything look.
  const isChip = variant === 'chip';
  // Bars: neutral `--color-bar` at 35% opacity matches the Traffic
  // page's `KpiSparkBars` watermark exactly so the two surfaces use
  // the same visual vocabulary for spark indicators.
  const fill = isChip ? 'color-mix(in srgb, var(--color-bar) 35%, transparent)' : bandFill(band);
  // Per-bar threshold colouring wins over the tile-wide fill.
  const perBarFill = barBand ? (d: { y: number }) => bandChartFill(barBand(d.y)) : undefined;
  // Empty state: every bar is null (or the array is empty). Previously
  // we'd hand this to CondensedBars which emits a blank `<svg/>`, so
  // an empty tile read as "label + dash + nothing". Render the same
  // `PlaceholderBars` watermark the real-user `PerformanceEmptyTile`
  // uses, so the chart slot keeps its SHAPE and the layout doesn't
  // jump when data lands and the bars swap in. Also softens the hero
  // value tone to match the watermark — "label + faint dash + faint
  // bars" reads as "data will go here" instead of "broken".
  const hasBars = bars.some((b) => b.y != null);
  // Hero value: neutral ink in chip mode (the chip carries the
  // colour signal); band-tinted in legacy mode. Falls back to a
  // muted tone when there's no data — same vocabulary as the
  // real-user empty tile.
  const valueTone = hasBars
    ? isChip
      ? 'text-[var(--color-ink)]'
      : bandText(band)
    : 'text-[var(--color-mute)]/70';
  const deltaGood = deltaPct == null || deltaPct === 0 ? null : deltaPct < 0 === lowerIsBetter;

  const body = (
    <div
      className={cn(
        // `group/tile` scopes the hover-reveal target so the explore
        // icon below only appears when THIS tile is hovered (not a
        // parent group).
        'group/tile rounded-xl border bg-[var(--color-paper)] p-4 transition-colors',
        selected
          ? 'border-[var(--color-line-strong)] bg-[var(--color-paper-alt)]/40'
          : 'border-[var(--color-line)] hover:border-[var(--color-line-strong)]',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="truncate text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
          {label}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {isChip && band !== 'unknown' && <StatusChip band={band} />}
          {deltaPct != null && Number.isFinite(deltaPct) && (
            <DeltaPill pct={deltaPct} good={deltaGood} />
          )}
        </div>
      </div>
      <div className={cn('mt-1.5 text-2xl font-semibold leading-none tabular-nums', valueTone)}>
        {value}
      </div>
      <div className="mt-3">
        {!hasBars ? (
          <PlaceholderBars />
        ) : chart === 'line' ? (
          // Taller than the bar slot (20px): a 2px-tall line reads as a
          // rule rather than a trend, and the surrounding tile has the
          // room. `preserveAspectRatio="none"` via the w-full class +
          // viewBox lets it stretch to the tile width.
          <Sparkline
            data={bars}
            width={400}
            height={32}
            stroke={bandChartFill(band)}
            fill={bandChartFill(band)}
            fillOpacity={0.12}
            ariaLabel={`${label} history`}
            className="w-full"
          />
        ) : (
          <CondensedBars<TMeta>
            data={bars}
            width={400}
            height={20}
            fill={fill}
            ariaLabel={`${label} history`}
            className="w-full"
            {...(perBarFill ? { barFill: perBarFill } : {})}
            {...(onBarClick ? { onBarClick } : {})}
            {...(barTitle ? { barTitle } : {})}
          />
        )}
      </div>
      {compare && (
        <div className="mt-3 flex items-center justify-between gap-2 border-[var(--color-line-soft)] border-t pt-2.5">
          <span className="flex min-w-0 items-center gap-1.5 text-[11px] text-[var(--color-mute)]">
            {/* Faint swatch — mirrors the guest's faint reference line
                on the trend charts so the encoding carries over. */}
            <span
              aria-hidden="true"
              className="inline-block w-3 shrink-0 border-t opacity-40"
              style={{ borderColor: compare.swatchColor }}
            />
            <span className="truncate">{compare.label}</span>
          </span>
          <span className="shrink-0 text-xs font-semibold tabular-nums text-[var(--color-ink-soft)]">
            {compare.value}
          </span>
        </div>
      )}
    </div>
  );

  // Legacy `onClick` path — tile-wide button. Only used by callers
  // that don't have `onBarClick` (nested buttons aren't valid).
  if (onClick && !onBarClick) {
    return (
      // `aria-label` only, no `title`: callers already hang a hover
      // tooltip (sample counts) on the wrapping element, and a nested
      // title would shadow it.
      <button
        type="button"
        onClick={onClick}
        {...(actionLabel ? { 'aria-label': actionLabel } : {})}
        className={cn(
          'block w-full cursor-pointer text-left outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-brand)]/40',
          className,
        )}
      >
        {body}
      </button>
    );
  }
  return <div className={className}>{body}</div>;
}

/**
 * Small band-coloured chip shown in the top-right corner of a tile
 * when `variant='chip'`. Surface area is intentionally tiny
 * (~6 px dot + 30-50 px label, ~18 px tall) so that across a row of
 * five tiles the colour reads as a status strip rather than as a
 * wall of pass/fail tint. Visually rhymes with `DeltaPill` and the
 * `StatusBadge` audit-status pill — same rounded-full, padded-px,
 * 10 px uppercase pattern.
 */
export function StatusChip({ band }: { band: Exclude<MetricBand, 'unknown'> }): ReactElement {
  const tone =
    band === 'good'
      ? 'bg-[var(--color-success-soft)] text-[var(--color-success)]'
      : band === 'needs-improvement'
        ? 'bg-[var(--color-warning-soft)] text-[var(--color-warning)]'
        : 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]';
  const dotColour =
    band === 'good'
      ? 'bg-[var(--color-success)]'
      : band === 'needs-improvement'
        ? 'bg-[var(--color-warning)]'
        : 'bg-[var(--color-danger)]';
  return (
    <span
      className={cn(
        // `whitespace-nowrap`: "Needs work" wrapped to two lines in the
        // sessions list's fixed-height rows.
        'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.04em]',
        tone,
      )}
    >
      <span aria-hidden="true" className={cn('inline-block size-1.5 rounded-full', dotColour)} />
      {BAND_LABEL[band]}
    </span>
  );
}

function DeltaPill({ pct, good }: { pct: number; good: boolean | null }): ReactElement {
  const sign = pct > 0 ? '+' : '';
  const tone =
    good === null
      ? 'bg-[var(--color-paper-alt)] text-[var(--color-mute)]'
      : good
        ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
        : 'bg-[var(--color-danger)]/10 text-[var(--color-danger)]';
  const Icon = pct === 0 ? Minus : pct > 0 ? ArrowUp : ArrowDown;
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums',
        tone,
      )}
    >
      <Icon aria-hidden="true" size={10} weight="bold" />
      {sign}
      {pct.toFixed(1)}%
    </span>
  );
}
